import React, { useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  SubCondition,
  ConditionQuantifier,
  HUB_MAX_SUB_CONDITIONS,
} from 'dorian-shared/types/branch/HubOption';
import type { IconCondition } from 'dorian-shared/types/branch/HubOption';
import { MemoryDTO } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { DragHandle } from 'components/ui/DragHandle/DragHandle';
import { useMemoryIconsContext } from 'contexts/MemoryIconsContext/MemoryIconsContext';
import { MemoryIconField } from '../../../../../../Book/MemoryBank/MemoryFields/MemoryIconField';
import { createEmptySubCondition } from '../../stateConditionDefaults';
import { SubConditionRow } from '../SubConditionRow';
import './IconConditionCard.scss';

interface IconConditionCardProps {
  condition: IconCondition;
  memoryVariables: MemoryDTO[];
  isDisabled: boolean;
  isValid: boolean;
  onChange: (updated: IconCondition) => void;
  onRemove: () => void;
}

export function IconConditionCard({
  condition,
  memoryVariables,
  isDisabled,
  isValid,
  onChange,
  onRemove,
}: IconConditionCardProps) {
  const { memoryIcons, createMemoryIcon } = useMemoryIconsContext();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: condition.uuid,
    disabled: isDisabled,
    transition: {
      duration: 250,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleQuantifierChange = useCallback(
    (q: ConditionQuantifier) => {
      onChange({ ...condition, quantifier: q });
    },
    [condition, onChange],
  );

  const handleSubConditionChange = useCallback(
    (index: number, updated: SubCondition) => {
      const next = [...condition.subConditions];
      next[index] = updated;
      onChange({ ...condition, subConditions: next });
    },
    [condition, onChange],
  );

  const handleSubConditionRemove = useCallback(
    (index: number) => {
      if (condition.subConditions.length <= 1) return;
      const next = condition.subConditions.filter((_, i) => i !== index);
      onChange({ ...condition, subConditions: next });
    },
    [condition, onChange],
  );

  const handleAddSubCondition = useCallback(() => {
    if (condition.subConditions.length >= HUB_MAX_SUB_CONDITIONS) return;
    onChange({
      ...condition,
      subConditions: [...condition.subConditions, createEmptySubCondition()],
    });
  }, [condition, onChange]);

  const handleIconChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const iconId = e.target.value ? Number(e.target.value) : null;
      onChange({ ...condition, resultIconId: iconId });
    },
    [condition, onChange],
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`icon-condition-card p-2${!isValid ? ' icon-condition-card--error' : ''}`}
      data-testid="icon-condition-card"
    >
      <div className="d-flex align-items-start">
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          disabled={isDisabled}
          className="mr-1"
        />

        <div className="flex-grow-1">
          <div className="icon-condition-card__conditions">
            {condition.subConditions.length > 1 && (
              <div className="d-flex align-items-center mb-2">
                <label className="mb-0 small mr-1" style={{ fontWeight: 600 }}>When</label>
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className={`btn btn-sm ${condition.quantifier === ConditionQuantifier.All ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handleQuantifierChange(ConditionQuantifier.All)}
                    disabled={isDisabled}
                  >
                    ALL
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${condition.quantifier === ConditionQuantifier.Any ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handleQuantifierChange(ConditionQuantifier.Any)}
                    disabled={isDisabled}
                  >
                    ANY
                  </button>
                </div>
                <label className="mb-0 small ml-1">of these are true:</label>
              </div>
            )}

            {condition.subConditions.map((sub, idx) => (
              <SubConditionRow
                key={sub.uuid}
                subCondition={sub}
                memoryVariables={memoryVariables}
                isDisabled={isDisabled}
                canRemove={condition.subConditions.length > 1}
                onChange={(updated) => handleSubConditionChange(idx, updated)}
                onRemove={() => handleSubConditionRemove(idx)}
              />
            ))}

            {condition.subConditions.length < HUB_MAX_SUB_CONDITIONS && (
              <button
                type="button"
                className="btn btn-outline-info btn-sm mt-2"
                style={{ fontSize: '0.7rem' }}
                onClick={handleAddSubCondition}
                disabled={isDisabled}
              >
                + Sub-condition
              </button>
            )}
          </div>

          {/* Result: icon picker */}
          <div className="icon-condition-card__result">
            <div className="icon-condition-card__result-field">
              <span className="icon-condition-card__result-field-label">
                <span className="icon-condition-card__result-arrow">→</span> Show icon
              </span>
              <div className="icon-field-inline">
                <MemoryIconField
                  id={`icon-override-${condition.uuid}`}
                  label=""
                  memoryIcons={memoryIcons}
                  onSave={createMemoryIcon}
                  value={condition.resultIconId != null ? String(condition.resultIconId) : ''}
                  onChange={handleIconChange}
                  disabled={isDisabled}
                  variant="outline-primary"
                />
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-outline-danger btn-sm ml-2"
          onClick={onRemove}
          disabled={isDisabled}
          title="Remove condition"
        >
          ×
        </button>
      </div>
    </div>
  );
}
